import { getHome, getMe, getRecommendations, requestEmailChange, type HomeRead } from '../api/endpoints.js';
import { useAction, useApi } from '../api/hooks.js';
import { localActionsFor, type RecommendedAction } from '../flow/actions.js';
import { monitoringStory } from '../flow/monitoring.js';
import { recommendationHref } from '../flow/recommendations.js';
import { Async, Badge, Failure } from '../ui/kit.js';

/**
 * Mobile screen 21 — where every branch converges. One composed read
 * (GET /v1/home) plus GET /v1/me for the greeting.
 *
 * The recommended-actions list is now two things, concatenated:
 *  1. `localActionsFor` (flow/actions.ts) — the two cards
 *     GET /v1/recommendations cannot provide (keep_plan, finish_setup).
 *  2. GET /v1/recommendations itself, rendered VERBATIM — same rule
 *     screens/protection.tsx already follows for its "Threats & actions"
 *     list: title/description/urgent/target come from the server and this
 *     screen invents no copy and re-derives no band from a number. Spec
 *     §4.9 called for this; the client no longer runs a second, disagreeing
 *     implementation of the business catalog (flow/actions.ts's docstring
 *     has the two ways it used to disagree).
 */

const BAND_COLORS: Record<string, string> = {
  low: '#39BEB7',
  moderate: '#FFB020',
  high: '#FF3B5C',
};

/**
 * What the covering subscription's ending state means for the person, in
 * words. Derived from the dates the home read carries, never asserted: a
 * cancel-at-period-end pending is the one case that used to be invisible here
 * — "monitoring is live, 1/1 covered" while coverage ended the next morning.
 */
function coverageNotice(
  subscription: HomeRead['subscription'],
): { tone: 'warn' | 'info'; title: string; detail: string } | null {
  if (subscription === null) return null;
  if (subscription.cancel_at !== null) {
    return {
      tone: 'warn',
      title: `Your plan is set to end on ${new Date(subscription.cancel_at).toLocaleDateString()}`,
      detail: 'Monitoring stops that day and no further scans run. Keep your plan to stay protected.',
    };
  }
  if (subscription.status === 'past_due') {
    return {
      tone: 'warn',
      title: 'Your last payment did not go through',
      detail: 'Coverage is paused until it does — update your card on the billing screen.',
    };
  }
  if (subscription.status === 'trialing' && subscription.trial_ends_at !== null) {
    return {
      tone: 'info',
      title: `Your free trial ends on ${new Date(subscription.trial_ends_at).toLocaleDateString()}`,
      detail: 'Your plan continues after that and the first charge is made then.',
    };
  }
  return null;
}

export function DashboardScreen() {
  const home = useApi(getHome, []);
  const me = useApi(getMe, []);
  const recommendations = useApi(getRecommendations, []);
  const resend = useAction((email: string) => requestEmailChange(email));

  return (
    <Async state={me}>
      {(person) => {
        const firstName = person.person.first_name ?? 'there';
        const score = home.data?.score ?? null;
        // The band arrives server-labelled with its noun ("low risk"); strip
        // the noun only to key the colour lookup, and render the label as-is.
        const bandColor =
          score === null ? '#C4BAEF' : (BAND_COLORS[score.band.replace(/ risk$/, '')] ?? '#FFB020');
        const subscription = home.data?.subscription ?? null;
        const notice = coverageNotice(subscription);

        // Local cards first (a lapsing plan and unfinished onboarding both
        // pre-date having anything to recommend), then the server's own list,
        // rendered verbatim. `EmailVerification` is the one target with no
        // href (flow/recommendations.ts) — it becomes a button wired to the
        // same resend action the old client-only `verify_email` card used,
        // never a re-authored card.
        const serverActions: RecommendedAction[] = (recommendations.data?.recommendations ?? []).map((rec) => {
          const isEmailVerify = rec.target === 'EmailVerification';
          return {
            id: rec.id,
            title: rec.title,
            description: rec.description,
            urgent: rec.urgent,
            href: recommendationHref(rec.target),
            run: isEmailVerify
              ? () => {
                  // The pending address first: for a first-time user `email`
                  // is null until the link is clicked, which is exactly when
                  // a resend is wanted.
                  const address = person.person.pending_email ?? person.person.email;
                  if (address !== null) resend.run(address);
                }
              : undefined,
            running: isEmailVerify ? resend.pending : undefined,
          };
        });
        const actions: RecommendedAction[] = [...localActionsFor(person, subscription), ...serverActions];

        return (
          <div className="dash">
            <h1 className="dash-greeting">Hi, {firstName}</h1>

            {notice !== null && (
              <div className={`dash-banner ${notice.tone}`} role="status">
                <strong>{notice.title}</strong>
                <span>{notice.detail}</span>
              </div>
            )}

            <div className="score-card">
              <p className="score-card-label">Your Likeness Health Score℠</p>
              {score === null ? (
                <div>
                  <p className="flow-sub" style={{ marginTop: 0 }}>
                    No score yet — take the Likeness Health Quiz to find out how exposed your
                    likeness is.
                  </p>
                  <a className="btn-primary btn-link" href="#/quiz">
                    Take the Likeness Health Quiz
                  </a>
                </div>
              ) : (
                <div className="dash-score-row">
                  <span className="score-number" style={{ color: bandColor }}>{score.live}</span>
                  <span>
                    <Badge>{score.band}</Badge>
                    <span className="flow-note" style={{ display: 'block', margin: '6px 0 0' }}>
                      of a possible {score.current_ceiling} today · computed {new Date(score.computed_at).toLocaleDateString()}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {actions.length > 0 && (
              <div className="score-card">
                <p className="score-card-label">Recommended actions</p>
                <div className="dash-actions">
                  {actions.map((action) => {
                    const body = (
                      <>
                        <span className="dash-action-text">
                          <strong>
                            {action.title}
                            {action.urgent && <span className="urgent-badge">Urgent</span>}
                          </strong>
                          <span>{action.description}</span>
                        </span>
                        <span className="choice-chevron" aria-hidden="true">
                          ›
                        </span>
                      </>
                    );
                    return action.href !== undefined ? (
                      <a key={action.id} className="dash-action" href={action.href}>
                        {body}
                      </a>
                    ) : (
                      <button
                        key={action.id}
                        type="button"
                        className="dash-action"
                        onClick={action.run}
                        disabled={action.running === true}
                      >
                        {body}
                      </button>
                    );
                  })}
                </div>
                {resend.result !== null && (
                  <p className="flow-note">Verification email sent — check the inbox.</p>
                )}
                {resend.error !== null && <Failure error={resend.error} />}
              </div>
            )}

            {home.error !== null && <Failure error={home.error} />}
            {recommendations.error !== null && <Failure error={recommendations.error} />}
            {home.data !== null && (
              <div className="dash-grid">
                <a className="score-card dash-tile" href="#/family">
                  <p className="score-card-label">Household</p>
                  <p className="dash-tile-big">
                    {home.data.household_summary.covered}/{home.data.household_summary.members}
                  </p>
                  <p className="flow-note" style={{ margin: 0 }}>
                    members covered
                    {/* Null while the reports surface is locked (0023): the
                        count is withheld, not zeroed. */}
                    {(home.data.household_summary.unresolved_matches ?? 0) > 0 &&
                      ` · ${String(home.data.household_summary.unresolved_matches)} unresolved matches`}
                  </p>
                </a>
                {(() => {
                  // Copy derived from the scan facts, never asserted: right
                  // after photo upload this reads "first scan is queued", not
                  // a flat "0 active hits" that deflates the whole journey.
                  const summary = home.data.reports_summary;
                  const locked = home.data.entitlement.reports === 'locked';
                  const story = monitoringStory({
                    scanState: summary.scan_state,
                    lastRunAt: summary.last_run_at,
                    firstScanCompletedAt: summary.first_scan_completed_at,
                    monitoredSources: summary.monitored_sources,
                    nextScanAfter: summary.next_scan_after,
                    unavailableReason: summary.unavailable_reason,
                    // Null while locked (0023). Zero is the honest input for
                    // the story: it renders the scan state, and the tile says
                    // "locked" instead of a count we were not given.
                    activeHits: summary.active_hits ?? 0,
                    liveExposureCount: summary.live_exposure_count ?? 0,
                  });
                  if (locked) {
                    return (
                      <a className="score-card dash-tile" href="#/reports">
                        <p className="score-card-label">Monitoring</p>
                        <p className="dash-tile-strong">Your report is ready</p>
                        <p className="flow-note" style={{ margin: 0 }}>
                          Subscribe to unlock it — scanning is already running.
                        </p>
                      </a>
                    );
                  }
                  return (
                    <a className="score-card dash-tile" href="#/reports">
                      <p className="score-card-label">Monitoring</p>
                      {story.tone === 'alert' ? (
                        <p className="dash-tile-big" style={{ color: '#FF3B5C' }}>
                          {summary.active_hits}
                        </p>
                      ) : (
                        <p className="dash-tile-strong">{story.headline}</p>
                      )}
                      <p className="flow-note" style={{ margin: 0 }}>
                        {story.tone === 'alert' ? story.headline : story.detail}
                      </p>
                    </a>
                  );
                })()}
              </div>
            )}
            {home.data !== null && (
              <p className="flow-note">{home.data.reports_summary.scope_note}</p>
            )}
          </div>
        );
      }}
    </Async>
  );
}
